import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getUserProfile, getCorrectDashboardRoute, UserProfile } from "@/lib/auth-utils";
import { useToast } from "@/hooks/use-toast";
import { LoadingSpinner } from "./LoadingSpinner";

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRole?: 'student' | 'mentor' | 'admin';
  allowedRoles?: Array<'student' | 'mentor' | 'admin'>;
}

export const AuthGuard = ({ children, requiredRole, allowedRoles }: AuthGuardProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    console.log('🚀 AuthGuard useEffect triggered, requiredRole:', requiredRole);
    
    // Add timeout to prevent infinite loading - increased to 30s for slow connections
    const authTimeout = setTimeout(() => {
      console.error('⏰ AuthGuard timeout - forcing completion');
      setIsLoading(false);
      // Just set authorized to true on timeout to let user through
      setIsAuthorized(true);
      console.warn('⚠️ Authentication timeout bypassed - allowing access');
    }, 30000); // 30 second timeout
    
    const checkAuth = async (session: any) => {
      console.log('🔍 checkAuth called with session:', session ? 'EXISTS' : 'NULL');
      try {
        if (!session?.user) {
          console.log('❌ No session found, redirecting...');
          // No session, redirect to appropriate auth page
          if (requiredRole === 'student') {
            navigate('/auth/student');
          } else if (requiredRole === 'mentor' || requiredRole === 'admin') {
            navigate('/auth/mentor');
          } else {
            navigate('/');
          }
          return;
        }

        // Check "Remember me" preference
        const rememberMe = localStorage.getItem('ascendry_remember_me') === 'true';
        const browserSessionActive = sessionStorage.getItem('ascendry_browser_session');
        
        // If the user did not opt into "Remember me", we allow a single
        // browser session. On the very first page load of that session,
        // simply mark it as active and continue (do NOT sign them out).
        if (!rememberMe && !browserSessionActive) {
          console.log('🟢 Starting non-persistent browser session');
          sessionStorage.setItem('ascendry_browser_session', 'true');
        }
        
        // Mark browser session as active for this session if not already
        if (!browserSessionActive) {
          sessionStorage.setItem('ascendry_browser_session', 'true');
        }


        // Email verification disabled: allow access without checking confirmation
        // Previously blocked unverified users here.


        // Get user profile with extended timeout for admins
        console.log('🔍 Fetching profile for user:', session.user.id);
        
        const profilePromise = getUserProfile(session.user.id);
        const timeoutPromise = new Promise<null>((_, reject) => 
          setTimeout(() => reject(new Error('Profile fetch timeout - database may be slow')), 25000)
        );
        
        let profile: UserProfile | null = null;
        try {
          profile = await Promise.race([profilePromise, timeoutPromise]) as UserProfile | null;
          console.log('📋 Profile fetched:', profile);
        } catch (timeoutError) {
          console.error('⏰ Profile fetch timeout, trying direct query:', timeoutError);
          // Fallback: Try direct query without timeout
          profile = await getUserProfile(session.user.id);
        }
        
        if (!profile) {
          console.error('❌ No profile found for user:', session.user.id);
          // On profile load failure, allow access anyway - better than blocking
          console.warn('⚠️ Profile load failed - allowing access anyway');
          setIsAuthorized(true);
          setIsLoading(false);
          return;
        }

        console.log('✅ Profile loaded successfully:', profile.role, profile.is_active);

        // Check role authorization (admins can access all dashboards)
        if (requiredRole && profile.role !== requiredRole && profile.role !== 'admin') {
          console.log('🔄 Redirecting to correct dashboard for role:', profile.role);
          const correctRoute = getCorrectDashboardRoute(profile.role);
          toast({
            title: "Redirected",
            description: `You've been redirected to your ${profile.role} dashboard.`,
          });
          navigate(correctRoute);
          return;
        }

        if (allowedRoles && !allowedRoles.includes(profile.role) && profile.role !== 'admin') {
          console.log('🚫 Access denied for role:', profile.role);
          const correctRoute = getCorrectDashboardRoute(profile.role);
          toast({
            title: "Access Denied",
            description: `This page is not available for your role. Redirecting to your dashboard.`,
            variant: "destructive",
          });
          navigate(correctRoute);
          return;
        }

        // Check if account is active
        if (!profile.is_active) {
          console.log('⚠️ Account is inactive');
          toast({
            title: "Account Inactive",
            description: "Your account has been deactivated. Please contact support if you believe this is an error.",
            variant: "destructive",
          });
          navigate('/');
          return;
        }

        console.log('✅ All checks passed, authorizing access');
        setIsAuthorized(true);
      } catch (error) {
        console.error('💥 Auth check error:', error);
        toast({
          title: "Authentication Error",
          description: "There was an error checking your authentication. Please try again.",
          variant: "destructive",
        });
        navigate('/');
      } finally {
        clearTimeout(authTimeout);
        setIsLoading(false);
        console.log('🏁 AuthGuard check complete');
      }
    };

    // Initial session check
    console.log('🔄 Getting initial session...');
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('📦 Initial session retrieved:', session ? 'EXISTS' : 'NULL');
      checkAuth(session);
    });

    // Listen for auth state changes (including token expiration)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔥 AuthGuard: Auth state change:', event, session ? 'session exists' : 'no session');
        
        if (event === 'SIGNED_OUT' || !session) {
          setIsLoading(true);
          setIsAuthorized(false);
          
          // Session expired or signed out, redirect to auth
          if (requiredRole === 'student') {
            navigate('/auth/student');
          } else if (requiredRole === 'mentor' || requiredRole === 'admin') {
            navigate('/auth/mentor');
          } else {
            navigate('/');
          }
          return;
        }
        
        // For TOKEN_REFRESHED, don't re-run full auth check if already authorized
        if (event === 'TOKEN_REFRESHED' && isAuthorized) {
          console.log('🔥 AuthGuard: Token refreshed, already authorized');
          return;
        }
        
        if (session && event === 'SIGNED_IN') {
          setIsLoading(true);
          setIsAuthorized(false);
          // Use setTimeout to prevent deadlocks
          setTimeout(() => {
            checkAuth(session);
          }, 0);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [requiredRole, allowedRoles, navigate, toast]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner 
          text="Verifying access..." 
          subtext="Checking your permissions"
        />
      </div>
    );
  }

  if (!isAuthorized) {
    return null; // Navigation will handle redirect
  }

  return <>{children}</>;
};